/**
 * SideDrawer: a side column that is either all there or not there at all, and
 * the one handle that opens and shuts it.
 *
 * Founder, 5 Oct 2026: a shut sidebar is worth nothing, so on a laptop it goes
 * the way it does on a phone and the page gets the whole width. The handle
 * stays where the hand already is, HALFWAY DOWN the column's edge, where the
 * Act-now rail keeps its knob too. Never move it to the top: #2091 did on
 * 27 Sep, and the founder had to ask for it back.
 *
 * - It rides the edge. Shut, it is a lit sliver on the screen's left edge;
 *   open, a grip on the column's border. The column slides and the handle
 *   comes with it, because the handle lives inside the sliding layer.
 * - It notices you. The sliver brightens as the pointer comes near the edge.
 * - Hover or focus, and the grip bends into a chevron pointing the way it
 *   will go, with a word beside it saying what pressing it does.
 * - Pull it. Drag the handle and the column follows the hand; let go and it
 *   snaps open or shut.
 * - `[` opens and shuts it from the keyboard, outside a text field, and `]`
 *   opens or shuts it together with Act Now (panellock).
 * - The clock under the handle keeps it shut for 5 minutes to 3 hours, through
 *   reloads and client switches (panellock, 19 Oct 2026). Opening it by hand
 *   ends the lock.
 *
 * Speed: the page takes its new width once; only the column moves, by
 * transform (UI_UX_CHECKLIST rule 12). Under less motion it jumps instead of
 * sliding and only the nav fades; the handle always stays in sight.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { usePrefersReducedMotion } from '@/routes/reducedMotion';
import { unlockPanel, usePanelLock } from '@/shared/panelLock/panelLock';
import { LazyPanelLockButton as PanelLockButton } from '@/shared/panelLock/LazyPanelLockButton';

const WIDTH = 240;
/** How close (px) the pointer has to come to the shut edge to wake the handle. */
const NEAR = 72;

interface SideDrawerProps {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    /** What pressing the handle does, for screen readers, e.g. "Expand menu". */
    openLabel: string;
    closeLabel: string;
    /** The word shown beside the handle on hover: "Menu", "Books". */
    name: string;
    children: ReactNode;
}

/** md and up, where the column is in the row (AppLayout's `hidden md:block`). */
function wide(): boolean {
    return typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 768px)').matches;
}

function typingIn(t: EventTarget | null): boolean {
    const el = t as HTMLElement | null;
    return Boolean(el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)));
}

export function SideDrawer({ open: wanted, onOpenChange: setWanted, openLabel, closeLabel, name, children }: SideDrawerProps) {
    const reduced = usePrefersReducedMotion();
    // panellock: a lock keeps it shut whatever the owner wants; a hand that
    // opens it ends the lock.
    const lock = usePanelLock('menu');
    const { until, unlock } = lock;
    const open = wanted && !until;
    const onOpenChange = useCallback((next: boolean) => {
        if (next) unlockPanel('menu');
        setWanted(next);
    }, [setWanted]);
    const boxRef = useRef<HTMLDivElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLButtonElement>(null);
    const drag = useRef<{ x: number; from: number; moved: boolean } | null>(null);
    const swallowClick = useRef(false);
    const [pulling, setPulling] = useState(false);

    // The column is in the page only while open or on its way: shut, there is
    // nothing behind the handle (the nav does not render off screen). It
    // mounts the moment it is asked for and leaves once the slide is over.
    const [present, setPresent] = useState(open);
    if ((open || pulling) && !present) setPresent(true);
    useEffect(() => {
        if (open || !present || pulling) return;
        const t = window.setTimeout(() => setPresent(false), reduced ? 160 : 440);
        return () => window.clearTimeout(t);
    }, [open, present, pulling, reduced]);

    // `[` toggles. Skipped on a phone (the desktop column is mounted there but
    // display:none) and while the person is typing.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
            if (typingIn(e.target) || !wide()) return;
            e.preventDefault();
            onOpenChange(!open);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onOpenChange]);

    // Shut: wake the sliver as the pointer nears it. One rect read per frame at
    // most, and only while the pointer is near the left of the screen.
    useEffect(() => {
        const el = handleRef.current;
        if (open || !el) return;
        let near = false;
        let raf = 0;
        const set = (now: boolean) => { if (now !== near) { near = now; el.dataset.near = String(now); } };
        const onMove = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return;
            if (e.clientX > NEAR * 3) { set(false); return; }
            if (raf) return;
            const { clientX: x, clientY: y } = e;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const r = el.getBoundingClientRect();
                set(x - r.left < NEAR && Math.abs(y - (r.top + r.height / 2)) < 180);
            });
        };
        window.addEventListener('pointermove', onMove, { passive: true });
        return () => {
            window.removeEventListener('pointermove', onMove);
            cancelAnimationFrame(raf);
            delete el.dataset.near;
        };
    }, [open]);

    // ─── Pull it ───
    // `translate`, not `transform`: Tailwind 4's translate-x-* classes set the
    // translate property, and the two would add up.
    const shift = (px: number) => { if (layerRef.current) layerRef.current.style.translate = `${px - WIDTH}px 0`; };

    const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0) return;
        drag.current = { x: e.clientX, from: open ? WIDTH : 0, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        if (!d.moved) {
            if (Math.abs(dx) < 5) return;
            d.moved = true;
            // The hand moves it now, not a transition.
            if (layerRef.current) layerRef.current.style.transition = 'none';
            handleRef.current?.setAttribute('data-dragging', '');
            setPulling(true);
        }
        shift(Math.max(0, Math.min(WIDTH, d.from + dx)));
    };
    const endDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
        const d = drag.current;
        drag.current = null;
        if (!d?.moved) return;
        swallowClick.current = true;
        const at = Math.max(0, Math.min(WIDTH, d.from + e.clientX - d.x));
        // A third of the way is enough either way: a pull is a decision.
        const next = d.from ? at > WIDTH * 0.66 : at > WIDTH * 0.33;
        const layer = layerRef.current;
        handleRef.current?.removeAttribute('data-dragging');
        setPulling(false);
        if (layer) {
            layer.style.transition = '';
            // Snap from where the hand left it to where it belongs.
            layer.style.translate = '';
        }
        if (next !== open) onOpenChange(next);
    };

    const onClick = () => {
        if (swallowClick.current) { swallowClick.current = false; return; }
        onOpenChange(!open);
    };

    return (
        <div ref={boxRef} className="relative z-20 h-full shrink-0" style={{ width: open ? WIDTH : 0 }}>
            <div
                ref={layerRef}
                // railcalm (10 Oct 2026): under less motion only the nav fades,
                // never this layer. The layer used to fade to opacity 0 when shut,
                // and the handle, which lives in it, faded with it: on a laptop
                // with Windows animations off the shut sidebar's handle was
                // invisible (a pointer on hover, nothing to see).
                className={`absolute inset-y-0 left-0 w-60 will-change-transform
                    ${reduced ? '' : 'transition-transform duration-[420ms] ease-[cubic-bezier(0.32,0.72,0,1)]'}
                    ${open ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div inert={!open} className={`h-full overflow-hidden ${reduced ? `transition-opacity duration-150 ${open ? 'opacity-100' : 'opacity-0'}` : ''}`}>
                    {present && children}
                </div>
                <button
                    ref={handleRef}
                    type="button"
                    onClick={onClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    aria-label={open ? closeLabel : openLabel}
                    aria-expanded={open}
                    data-open={open}
                    data-plain-kept="sidebar-toggle"
                    data-panel-toggle="menu"
                    data-calm={reduced || undefined}
                    className="side-handle"
                >
                    <span className="side-handle__pill" aria-hidden>
                        <span className="side-handle__bar side-handle__bar--up" />
                        <span className="side-handle__bar side-handle__bar--down" />
                    </span>
                    <span className="side-handle__tip" aria-hidden>
                        {open ? 'Hide' : name}
                        <kbd>[</kbd>
                    </span>
                </button>
                {/* panellock: the clock rides under the handle; locked and shut, it shows the time left. */}
                {open ? (
                    <PanelLockButton panel="menu" until={null} onLock={lock.lock} onUnlock={unlock} variant="edge" className="left-full top-[calc(50%+54px)] z-30 -translate-x-1/2" />
                ) : until ? (
                    <PanelLockButton panel="menu" until={until} onLock={lock.lock} onUnlock={unlock} variant="tag" className="left-full top-[calc(50%+36px)] z-30" />
                ) : null}
            </div>
        </div>
    );
}
